import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SERVER_URL } from "../../constant";
import AlertBox from "../../component/AlertBox";
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import DialogBox from "../../component/DialogBox";
import ButtonCompo from "../../component/ButtonCompo";
import InputField from "../../component/InputField";
import { GoFile } from "react-icons/go";
import { MdOutlineFileDownload } from "react-icons/md";
import { AiOutlineEdit } from "react-icons/ai";
import { AiOutlineDelete } from "react-icons/ai";
import { useSelector } from "react-redux";

function PolicyManagement() {
  const isEmployee = useSelector(
    (state) => state.employee?.isEmployeeAuthenticated
  );
  const employeeDataForAccess = useSelector((state) => state.employee?.employeeData);

  const navigate = useNavigate();

  //redirect to signin if not logged in
  useEffect(() => {
    if (isEmployee !== undefined && isEmployee === false) {
      navigate("/employee/signin");
    }
  }, [isEmployee]);

  //redirect to employee dashboard if permission is not given
  useEffect(() => {
    if (
      employeeDataForAccess !== null &&
      employeeDataForAccess?.admin?.policyManagement  === false
    ) {
      navigate("/employee/dashboard");
    }
  }, [employeeDataForAccess]);

  /* ----------------------------------------------------------------------------------------------------- */
  /* ----------------------------------------Main Coding Part--------------------------------------------- */
  /* ----------------------------------------------------------------------------------------------------- */
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [employeeData, setEmployeeData] = useState([]);
  const [policies, setPolicies] = useState([]);
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [reload, setReload] = useState(false);

  // fetch company data
  async function getEmployee() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/employee/company/get`,
        method: "get",
        withCredentials: true,
      });
      console.log(res.data?.data);
      setEmployeeData(res.data?.data);
      setPolicies(res.data?.data?.policies);
    } catch (error) {
      setOpen(true);
      setSuccess(false);
      setMessage("Employee fetch failed");
    } finally {
      setLoading(false);
    }
  }

  //add doc
  async function addDoc() {
    if (title?.length === 0 || description?.length === 0 || file === null) {
      setOpen(true);
      setSuccess(false);
      setMessage("Please fill all the fields and choose a file");
      return null;
    }
    const formData = new FormData();
    formData.append("policy", file);
    formData.append("title", title);
    formData.append("description", description);
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/company/policy/add`,
        method: "post",
        withCredentials: true,
        data: formData,
      });
      if (res.status >= 200 || res.status < 400) {
        setReload((prev) => !prev);
        setOpen(true);
        setSuccess(true);
        setMessage("Document added successfully");
      }
    } catch (error) {
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(false);
      setMessage(error?.response?.data?.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  // set edit value
  function setValue(data) {
    setTitle(data?.title);
    setDescription(data?.description);
  }

  //edit doc
  async function editDoc(prevTitle) {
    const formData = new FormData();
    formData.append("policy", file);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("prevTitle", prevTitle);
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/company/policy/edit`,
        method: "put",
        withCredentials: true,
        data: formData,
      });
      if (res.status >= 200 || res.status < 400) {
        setReload((prev) => !prev);
        setOpen(true);
        setSuccess(true);
        setMessage("Document edited successfully");
      }
    } catch (error) {
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(false);
      setMessage(error?.response?.data?.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  //delete doc
  async function deleteDoc(data) {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/company/policy/delete`,
        method: "delete",
        withCredentials: true,
        data: {
            fileData: {
                doc: data?.doc,
                title: data?.title,
            }
        },
      });
      if (res.status >= 200 || res.status < 400) {
        setReload((prev) => !prev);
        setOpen(true);
        setSuccess(true);
        setMessage("Document deleted successfully");
      }
    } catch (error) {
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(false);
      setMessage(error?.response?.data?.message);
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getEmployee();
  }, [reload]);

  return (
    <>
      {loading && (
        <LinearProgress className="!fixed !top-0 !left-0 !right-0 !h-[8px]" />
      )}
      <div className="w-full flex justify-center items-center">
        {success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        {!success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        <div className="w-[90vw] lg:w-[50rem] py-5">
          <div className="w-full">
            <div className="w-full mt-5">
              <h3 className="font-medium text-[25px]">Company Policies</h3>
              {policies === undefined || policies?.length === 0 ? (
                <div className="w-full flex justify-center items-center flex-col mt-5">
                  <p className="font-medium text-slate-400 mb-3">
                    No documents added yet
                  </p>
                  <DialogBox
                    trigger={<ButtonCompo>Add A File</ButtonCompo>}
                    title="Add Document"
                    actionButton={
                      <ButtonCompo onClick={addDoc}>Save</ButtonCompo>
                    }
                    closeActionButton={<ButtonCompo>close</ButtonCompo>}
                  >
                    <div className="w-full space-y-3">
                      <input
                        type="file"
                        className="hidden"
                        ref={fileRef}
                        onChange={(event) => setFile(event?.target?.files[0])}
                      />
                      <InputField
                        label="Title"
                        onChange={(event) => setTitle(event?.target?.value)}
                      />
                      <InputField
                        label="Description"
                        multiline
                        onChange={(event) =>
                          setDescription(event?.target?.value)
                        }
                      />
                      <section
                        onClick={() => fileRef.current.click()}
                        className="bg-slate-100/10 py-5 flex justify-center px-3 items-center w-full border border-dashed border-slate-400/80 rounded-[5px] cursor-pointer"
                      >
                        <p className="text-slate-800/80 text-[15.5px] flex flex-col justify-center items-center gap-1 font-medium">
                          Upload Document
                          {file !== null && (
                            <span className="text-slate-500">
                              1 document selected
                            </span>
                          )}
                        </p>
                      </section>
                    </div>
                  </DialogBox>
                </div>
              ) : (
                <div className="w-full flex justify-center items-center flex-col mt-5">
                  <div className="flex justify-end items-end w-full mb-3">
                    <DialogBox
                      trigger={<ButtonCompo>Add A File</ButtonCompo>}
                      title="Add Document"
                      actionButton={
                        <ButtonCompo onClick={addDoc}>Save</ButtonCompo>
                      }
                      closeActionButton={<ButtonCompo>close</ButtonCompo>}
                    >
                      <div className="w-full space-y-3">
                        <input
                          type="file"
                          className="hidden"
                          ref={fileRef}
                          onChange={(event) => setFile(event?.target?.files[0])}
                        />
                        <InputField
                          label="Title"
                          onChange={(event) => setTitle(event?.target?.value)}
                        />
                        <InputField
                          label="Description"
                          multiline
                          onChange={(event) =>
                            setDescription(event?.target?.value)
                          }
                        />
                        <section
                          onClick={() => fileRef.current.click()}
                          className="bg-slate-100/10 py-5 flex justify-center px-3 items-center w-full border border-dashed border-slate-400/80 rounded-[5px] cursor-pointer"
                        >
                          <p className="text-slate-800/80 text-[15.5px] flex flex-col justify-center items-center gap-1 font-medium">
                            Upload Document
                            {file !== null && (
                              <span className="text-slate-500">
                                1 document selected
                              </span>
                            )}
                          </p>
                        </section>
                      </div>
                    </DialogBox>
                  </div>
                  <Table>
                    <TableBody className="border">
                      {policies?.map((e, i) => (
                        <TableRow>
                          <TableCell>
                            <div className="flex gap-3 items-center">
                              <span className="bg-slate-200/50 p-2 rounded-full">
                                <GoFile className="text-[20px] text-slate-800" />
                              </span>
                              <div>
                                <p className="font-medium">{e?.title}</p>
                                <p className="text-slate-500">
                                  {e?.description}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="bg-slate-200/70 px-3 py-1 rounded-full text-slate-700 font-medium">
                              {new Date(e?.date).toLocaleDateString("en-US", {
                                timeZone: "Asia/Kolkata",
                              })}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Link
                              className="bg-slate-500"
                              to={`${SERVER_URL}/api/v1/file/view/${e?.doc}`}
                              target="_blank"
                            >
                              <MdOutlineFileDownload size={"20px"} />
                            </Link>
                          </TableCell>
                          <TableCell>
                            <DialogBox
                              trigger={
                                <div
                                  className="flex gap-3 items-center cursor-pointer"
                                  onClick={() => setValue(e)}
                                >
                                  <span className="bg-blue-200/50 p-2 rounded-full">
                                    <AiOutlineEdit className="text-[20px] text-slate-800" />
                                  </span>
                                </div>
                              }
                              title="Edit Document"
                              actionButton={
                                <ButtonCompo onClick={() => editDoc(e?.title)}>Save</ButtonCompo>
                              }
                              closeActionButton={
                                <ButtonCompo>close</ButtonCompo>
                              }
                            >
                              <div className="w-full space-y-3">
                                <input
                                  type="file"
                                  className="hidden"
                                  ref={fileRef}
                                  onChange={(event) =>
                                    setFile(event?.target?.files[0])
                                  }
                                />
                                <InputField
                                  label="Title"
                                  onChange={(event) =>
                                    setTitle(event?.target?.value)
                                  }
                                  value={title}
                                />
                                <InputField
                                  label="Description"
                                  multiline
                                  onChange={(event) =>
                                    setDescription(event?.target?.value)
                                  }
                                  value={description}
                                />
                                <section
                                  onClick={() => fileRef.current.click()}
                                  className="bg-slate-100/10 py-5 flex justify-center px-3 items-center w-full border border-dashed border-slate-400/80 rounded-[5px] cursor-pointer"
                                >
                                  <p className="text-slate-800/80 text-[15.5px] flex flex-col justify-center items-center gap-1 font-medium">
                                    Upload Document
                                    {file !== null && (
                                      <span className="text-slate-500">
                                        1 document selected
                                      </span>
                                    )}
                                  </p>
                                </section>
                              </div>
                            </DialogBox>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-3 items-center cursor-pointer" onClick={() => deleteDoc(e)}>
                              <span className="bg-red-200/50 p-2 rounded-full">
                                <AiOutlineDelete className="text-[20px] text-slate-800" />
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PolicyManagement;
